import { useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Captcha, CAPTCHA_MSG, RecaptchaNotice } from "./Captcha";
import { UnsubscribeDialog } from "./UnsubscribeDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SignupForm = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [unsubOpen, setUnsubOpen] = useState(false);
  const [token, setToken] = useState("");
  const captcha = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError("Please enter your email address.");
    if (!token) return setError(CAPTCHA_MSG);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email: email.trim(), name: name.trim(), recaptcha_token: token });
      setStatus(data?.status ?? null);
      if (!data?.status) setError("Something went wrong. Please try again.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Please enter a valid email address.");
    } finally {
      captcha.current?.reset();
      setToken("");
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-lg">
      <AnimatePresence mode="popLayout" initial={false}>
        {status === "pending" || status === "exists" ? (
          <motion.div key="done" data-testid="signup-success-box" initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="bg-[#ECEAE6] px-8 py-10">
            <p className="font-display text-3xl font-extrabold tracking-tight">{status === "exists" ? "You're Already on the List" : "Check Your Inbox"}</p>
            <p className="mt-3 text-base text-[#3a3836]" data-testid="signup-pending-message">
              {status === "exists"
                ? <><strong>{email.trim()}</strong> is already verified and on our mailing list. You'll hear from us with updates.</>
                : <>We've sent a verification link to <strong>{email.trim()}</strong>. Click it within 15 minutes to join the list.</>}
            </p>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} noValidate data-testid="signup-form" exit={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            transition={{ duration: 0.4 }} className="flex flex-col">
            <label htmlFor="signup-name" className="mb-3 text-sm font-bold">Full Name <span className="font-normal text-[#57534E]">(optional)</span></label>
            <input id="signup-name" type="text" value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
              placeholder="Your name" data-testid="signup-name-input" autoComplete="name"
              className="mb-6 w-full border border-[#E62314] bg-white/60 px-4 py-3.5 text-base text-black placeholder:text-[#8a8580] outline-none transition-shadow focus:ring-4 focus:ring-[#E62314]/15" />
            <label htmlFor="signup-email" className="mb-3 text-sm font-bold">Email Address <span className="text-[#E62314]">*</span></label>
            <input id="signup-email" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setStatus(null); }}
              placeholder="name@example.com" data-testid="signup-email-input" autoComplete="email"
              className="w-full border border-[#E62314] bg-white/60 px-4 py-3.5 text-base text-black placeholder:text-[#8a8580] outline-none transition-shadow focus:ring-4 focus:ring-[#E62314]/15" />
            <Captcha ref={captcha} onChange={setToken} testId="signup-recaptcha" />
            <div className="mt-8">
              <button type="submit" disabled={loading} data-testid="signup-submit-link"
                className="group inline-flex items-center gap-2 border-b-2 border-[#E62314] pb-1 text-lg font-extrabold text-black transition-colors hover:text-[#E62314] disabled:cursor-not-allowed disabled:opacity-40">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Get Updates
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
            </div>
            <RecaptchaNotice testId="signup-recaptcha-notice" className="mt-4" />
            {status === "exists" && (
              <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="signup-exists-message" className="mt-6 text-base font-semibold text-[#0A0A0A]">
                This email is already on our list. Thank you.
              </motion.p>
            )}
            {error && <p data-testid="signup-error-message" className="mt-6 text-sm font-semibold text-[#C21B0E]">{error}</p>}
          </motion.form>
        )}
      </AnimatePresence>
      <p className="mt-6 text-sm text-[#57534E]">
        Already on the list and want to leave?{" "}
        <button type="button" onClick={() => setUnsubOpen(true)} data-testid="signup-unsubscribe-link" className="font-semibold text-[#0A0A0A] underline underline-offset-4 hover:text-[#E62314]">Unsubscribe</button>
      </p>
      <UnsubscribeDialog open={unsubOpen} onOpenChange={setUnsubOpen} />
    </div>
  );
};
